import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Users, Search, Ban, CheckCircle, 
  Crown, Building, Briefcase, Mail, Download, Clock,
  FileText, CreditCard, Flag, Send, Key, Eye, AlertTriangle, RefreshCw, Trash2, UserCog, MailCheck
} from 'lucide-react';
import api from '../../services/api';

const AdminUsers = () => {
  const { user, loginAsUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [flaggedAccounts, setFlaggedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [accountType, setAccountType] = useState(searchParams.get('account_type') || 'all');
  const [status, setStatus] = useState('all');
  const [total, setTotal] = useState(0);
  const [flaggedTotal, setFlaggedTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState('');
  const [trialDays, setTrialDays] = useState(3);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showBulkTrialModal, setShowBulkTrialModal] = useState(false);
  const [bulkTrialDays, setBulkTrialDays] = useState(3);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [hardDelete, setHardDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [impersonating, setImpersonating] = useState(null);

  // One-click impersonation handler
  const handleViewAsUser = async (targetUser) => {
    if (targetUser.id === user.id) {
      toast.error("Can't impersonate yourself");
      return;
    }
    
    setImpersonating(targetUser.id);
    try {
      await loginAsUser(targetUser.id);
      toast.success(`Now viewing as ${targetUser.email}`);
      
      // Redirect to the user's dashboard
      const dashboardPath = targetUser.account_type === 'employer' 
        ? '/employer/dashboard' 
        : '/employee/dashboard';
      navigate(dashboardPath);
    } catch (error) {
      console.error('Impersonation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    } finally {
      setImpersonating(null);
    }
  };

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    if (activeTab === 'flagged') {
      fetchFlaggedAccounts();
    } else {
      fetchUsers();
    }
  }, [user, navigate, search, accountType, status, page, activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * 50,
        limit: 50
      };
      if (search) params.search = search;
      if (accountType !== 'all') params.account_type = accountType;
      if (status !== 'all') params.status = status;

      const res = await adminAPI.getUsers(params);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchFlaggedAccounts = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getFlaggedAccounts({ skip: page * 50, limit: 50 });
      setFlaggedAccounts(res.data.accounts || []);
      setFlaggedTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to load flagged accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async (userId) => {
    try {
      setProfileLoading(true);
      const res = await adminAPI.getUserFullProfile(userId);
      setProfileData(res.data);
      setShowProfileModal(true);
    } catch (error) {
      toast.error('Failed to load user profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSuspend = async (userId) => {
    try {
      await adminAPI.suspendUser(userId);
      toast.success('User suspended');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleUnsuspend = async (userId) => {
    try {
      await adminAPI.unsuspendUser(userId);
      toast.success('User unsuspended');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to unsuspend user');
    }
  };

  const handleUnflag = async (userId) => {
    try {
      await adminAPI.unflagUser(userId);
      toast.success('User unflagged');
      if (activeTab === 'flagged') {
        fetchFlaggedAccounts();
      } else {
        fetchUsers();
      }
    } catch (error) {
      toast.error('Failed to unflag user');
    }
  };

  const handleSendMessage = async () => {
    if (!selectedUser || !messageSubject || !messageContent) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await adminAPI.sendMessage(selectedUser.id, messageSubject, messageContent);
      toast.success('Message sent successfully');
      setShowMessageModal(false);
      setMessageSubject('');
      setMessageContent('');
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleTriggerPasswordReset = async (userId) => {
    try {
      await adminAPI.triggerPasswordReset(userId);
      toast.success('Password reset email sent');
    } catch (error) {
      toast.error('Failed to send password reset');
    }
  };

  const handleVerifyEmail = async (userId, email) => {
    try {
      const res = await adminAPI.verifyUserEmail(userId);
      if (res.data.already_verified) {
        toast.info('Email was already verified');
      } else {
        toast.success(`Email verified for ${email}`);
      }
      // Refresh the user list
      if (activeTab === 'flagged') {
        fetchFlaggedAccounts();
      } else {
        fetchUsers();
      }
      // Also refresh profile modal if open
      if (showProfileModal && selectedUser?.id === userId) {
        fetchUserProfile(userId);
      }
    } catch (error) {
      toast.error('Failed to verify email');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser || deleteConfirm !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    try {
      setDeleting(true);
      await api.delete(`/admin/users/${selectedUser.id}`, {
        params: { reason: deleteReason, hard_delete: hardDelete }
      });
      toast.success(`User ${hardDelete ? 'permanently deleted' : 'anonymized'} successfully`);
      setShowDeleteModal(false);
      setShowProfileModal(false);
      setSelectedUser(null);
      setDeleteReason('');
      setDeleteConfirm('');
      setHardDelete(false);
      if (activeTab === 'flagged') {
        fetchFlaggedAccounts();
      } else {
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await adminAPI.getUsers({ export: true });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vett_users_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    
    try {
      if (action === 'extend_trial') {
        setShowBulkTrialModal(true);
        return;
      }
      // Single action for each user since bulk endpoints may not exist
      for (const userId of selectedUsers) {
        if (action === 'suspend') {
          await adminAPI.suspendUser(userId);
        } else if (action === 'unsuspend') {
          await adminAPI.unsuspendUser(userId);
        }
      }
      toast.success(`${selectedUsers.length} users ${action === 'suspend' ? 'suspended' : 'unsuspended'}`);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error('Bulk action failed');
    }
  };

  const handleBulkExtendTrial = async () => {
    try {
      for (const userId of selectedUsers) {
        await adminAPI.updateSubscription(userId, 'trial', bulkTrialDays);
      }
      toast.success(`Extended trial for ${selectedUsers.length} users`);
      setShowBulkTrialModal(false);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to extend trials');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const handleUpdateSubscription = async () => {
    if (!selectedUser || !subscriptionTier) return;
    
    try {
      await adminAPI.updateSubscription(
        selectedUser.id,
        subscriptionTier,
        subscriptionTier === 'trial' ? trialDays : null
      );
      toast.success('Subscription updated');
      setShowSubscriptionModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const openSubscriptionModal = (u) => {
    setSelectedUser(u);
    if (u.account_type === 'employer') {
      setSubscriptionTier(u.profile?.subscription_tier || 'free');
    } else {
      setSubscriptionTier(u.profile?.is_pro ? 'pro' : 'free');
    }
    setShowSubscriptionModal(true);
  };

  const getSubscriptionBadge = (u) => {
    if (u.account_type === 'employer') {
      const tier = u.profile?.subscription_tier || 'free';
      const colors = {
        trial: 'bg-amber-500/20 text-amber-400',
        free: 'bg-gray-500/20 text-gray-400',
        starter: 'bg-blue-500/20 text-blue-400',
        growth: 'bg-purple-500/20 text-purple-400'
      };
      return <span className={`px-2 py-1 rounded text-xs ${colors[tier]}`}>{tier}</span>;
    } else {
      return u.profile?.is_pro 
        ? <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">Pro</span>
        : <span className="px-2 py-1 rounded text-xs bg-gray-500/20 text-gray-400">Free</span>;
    }
  };

  // Render users table
  const renderUsersTable = (userList, totalCount) => (
    <>
      {/* Filters */}
      <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
              <Input
                placeholder="Search by email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg"
                data-testid="user-search-input"
              />
            </div>
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[150px] bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
              <SelectItem value="all" className="text-[#0A0F1E]">All Status</SelectItem>
              <SelectItem value="active" className="text-[#0A0F1E]">Active</SelectItem>
              <SelectItem value="suspended" className="text-[#0A0F1E]">Suspended</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExportCSV} className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
        
        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#E8EDF5] flex items-center gap-4">
            <span className="text-[#5B6472] text-sm">{selectedUsers.length} selected</span>
            <Select value={bulkAction} onValueChange={(value) => { setBulkAction(value); handleBulkAction(value); }}>
              <SelectTrigger className="w-[200px] bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg">
                <SelectValue placeholder="Bulk Actions..." />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                <SelectItem value="suspend" className="text-[#0A0F1E]">Suspend Selected</SelectItem>
                <SelectItem value="unsuspend" className="text-[#0A0F1E]">Unsuspend Selected</SelectItem>
                <SelectItem value="extend_trial" className="text-[#0A0F1E]">Extend Trial</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" onClick={() => setSelectedUsers([])} className="text-[#5B6472]">
              Clear
            </Button>
          </div>
        )}
      </Card>

      {/* Users Table */}
      <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-[#1B5AFF] animate-spin" />
          </div>
        ) : userList.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F5F8FF]/50">
                  <tr>
                    <th className="p-4 w-12">
                      <Checkbox 
                        checked={selectedUsers.length === userList.length && userList.length > 0}
                        onCheckedChange={toggleSelectAll}
                        className="border-[#E8EDF5]"
                      />
                    </th>
                    <th className="text-left p-4 text-[#5B6472] text-sm font-medium">User</th>
                    <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Type</th>
                    <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Tier</th>
                    <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Subscription</th>
                    <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Verified</th>
                    <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Joined</th>
                    <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Status</th>
                    <th className="text-right p-4 text-[#5B6472] text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8EDF5]">
                  {userList.map((u) => (
                    <tr 
                      key={u.id} 
                      className="hover:bg-[#F5F8FF]/30 cursor-pointer" 
                      onClick={() => fetchUserProfile(u.id)}
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedUsers.includes(u.id)}
                          onCheckedChange={() => toggleUserSelection(u.id)}
                          className="border-[#E8EDF5]"
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                            {u.account_type === 'employer' 
                              ? <Building className="w-5 h-5 text-[#5B6472]" />
                              : <Briefcase className="w-5 h-5 text-[#5B6472]" />
                            }
                          </div>
                          <div>
                            <p className="text-[#0A0F1E] font-medium">{u.email}</p>
                            {u.profile?.company_name && (
                              <p className="text-[#5B6472] text-sm">{u.profile.company_name}</p>
                            )}
                            {u.profile?.first_name && (
                              <p className="text-[#5B6472] text-sm">
                                {u.profile.first_name} {u.profile.last_name}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          u.account_type === 'employer' 
                            ? 'bg-blue-500/20 text-blue-400' 
                            : 'bg-purple-500/20 text-purple-400'
                        }`}>
                          {u.account_type}
                        </span>
                      </td>
                      <td className="p-4">
                        {u.account_type === 'employee' && u.tier_status ? (
                          <span className={`px-2 py-1 rounded text-xs ${
                            u.tier_status.tier === 4 ? 'bg-amber-500/20 text-amber-400' :
                            u.tier_status.tier === 3 ? 'bg-purple-500/20 text-purple-400' :
                            u.tier_status.tier === 2 ? 'bg-blue-500/20 text-blue-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {u.tier_status.tier_name || 'Unverified'}
                          </span>
                        ) : (
                          <span className="text-[#5B6472]">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        {getSubscriptionBadge(u)}
                      </td>
                      <td className="p-4">
                        {u.email_verified 
                          ? <CheckCircle className="w-5 h-5 text-green-400" title="Email Verified" />
                          : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVerifyEmail(u.id, u.email);
                              }}
                              className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 px-2 py-1 h-auto"
                              title="Click to verify email"
                            >
                              <MailCheck className="w-4 h-4 mr-1" />
                              <span className="text-xs">Verify</span>
                            </Button>
                          )
                        }
                      </td>
                      <td className="p-4 text-[#5B6472] text-sm">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        {u.suspended 
                          ? <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-400">Suspended</span>
                          : u.flagged 
                            ? <span className="px-2 py-1 rounded text-xs bg-amber-500/20 text-amber-400">Flagged</span>
                            : <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">Active</span>
                        }
                      </td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2">
                          {/* VIEW AS BUTTON - One-click impersonation */}
                          {user?.is_superadmin && u.id !== user.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewAsUser(u)}
                              disabled={impersonating === u.id}
                              className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                              title="View As This User"
                              data-testid={`view-as-${u.id}`}
                            >
                              {impersonating === u.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <UserCog className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => fetchUserProfile(u.id)}
                            className="text-[#5B6472] hover:text-[#0A0F1E]"
                            title="View Profile"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openSubscriptionModal(u)}
                            className="text-[#5B6472] hover:text-[#0A0F1E]"
                            title="Manage Subscription"
                          >
                            <Crown className="w-4 h-4" />
                          </Button>
                          {u.suspended ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUnsuspend(u.id)}
                              className="text-green-400 hover:text-green-300"
                              title="Unsuspend"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSuspend(u.id)}
                              className="text-red-400 hover:text-red-300"
                              disabled={u.is_admin}
                              title="Suspend"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-[#E8EDF5]">
              <p className="text-[#5B6472] text-sm">
                Showing {page * 50 + 1} - {Math.min((page + 1) * 50, totalCount)} of {totalCount}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="border-[#E8EDF5] text-[#0A0F1E]"
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * 50 >= totalCount}
                  className="border-[#E8EDF5] text-[#0A0F1E]"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-16 text-[#5B6472]">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No users found</p>
            <p className="text-sm">Try adjusting your search or filters</p>
          </div>
        )}
      </Card>
    </>
  );

  // Render flagged accounts table
  const renderFlaggedTable = () => (
    <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-[#1B5AFF] animate-spin" />
        </div>
      ) : flaggedAccounts.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F5F8FF]/50">
                <tr>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">User</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Type</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Status</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Reason</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Date</th>
                  <th className="text-right p-4 text-[#5B6472] text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8EDF5]">
                {flaggedAccounts.map((u) => (
                  <tr key={u.id} className="hover:bg-[#F5F8FF]/30 cursor-pointer" onClick={() => fetchUserProfile(u.id)}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                          {u.account_type === 'employer' 
                            ? <Building className="w-5 h-5 text-[#5B6472]" />
                            : <Briefcase className="w-5 h-5 text-[#5B6472]" />
                          }
                        </div>
                        <div>
                          <p className="text-[#0A0F1E] font-medium">{u.email}</p>
                          {u.profile?.company_name && (
                            <p className="text-[#5B6472] text-sm">{u.profile.company_name}</p>
                          )}
                          {u.profile?.first_name && (
                            <p className="text-[#5B6472] text-sm">{u.profile.first_name} {u.profile.last_name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        u.account_type === 'employer' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                      }`}>
                        {u.account_type}
                      </span>
                    </td>
                    <td className="p-4">
                      {u.suspended ? (
                        <Badge className="bg-red-500/20 text-red-400 border-0">Suspended</Badge>
                      ) : (
                        <Badge className="bg-amber-500/20 text-amber-400 border-0">Flagged</Badge>
                      )}
                    </td>
                    <td className="p-4">
                      <span className="text-[#5B6472] text-sm max-w-xs truncate block">
                        {u.flag_reason || u.suspension_reason || 'No reason provided'}
                      </span>
                    </td>
                    <td className="p-4 text-[#5B6472] text-sm">
                      {new Date(u.flagged_at || u.suspended_at || u.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        {u.suspended ? (
                          <Button
                            size="sm"
                            onClick={() => handleUnsuspend(u.id)}
                            className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                          >
                            Unsuspend
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleUnflag(u.id)}
                              className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                            >
                              Unflag
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSuspend(u.id)}
                              className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            >
                              Suspend
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-[#E8EDF5]">
            <p className="text-[#5B6472] text-sm">
              Showing {page * 50 + 1} - {Math.min((page + 1) * 50, flaggedTotal)} of {flaggedTotal}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-[#E8EDF5] text-[#0A0F1E]"
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * 50 >= flaggedTotal}
                className="border-[#E8EDF5] text-[#0A0F1E]"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-16 text-[#5B6472]">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50 text-green-400" />
          <p className="text-lg">No flagged or suspended accounts</p>
          <p className="text-sm">All accounts are in good standing</p>
        </div>
      )}
    </Card>
  );

  return (
    <AdminLayout title="User Management" breadcrumbs={[{ label: 'Users' }]}>
      <div className="vds-admin-tabs">
        <button 
          className={`vds-admin-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => { setActiveTab('all'); setPage(0); }}
        >
          <Users className="w-4 h-4" />
          All Users
        </button>
        <button 
          className={`vds-admin-tab ${activeTab === 'employers' ? 'active' : ''}`}
          onClick={() => { setActiveTab('employers'); setPage(0); }}
        >
          <Building className="w-4 h-4" />
          Employers
        </button>
        <button 
          className={`vds-admin-tab ${activeTab === 'employees' ? 'active' : ''}`}
          onClick={() => { setActiveTab('employees'); setPage(0); }}
        >
          <Briefcase className="w-4 h-4" />
          Employees
        </button>
        <button 
          className={`vds-admin-tab ${activeTab === 'flagged' ? 'active' : ''}`}
          onClick={() => { setActiveTab('flagged'); setPage(0); }}
        >
          <AlertTriangle className="w-4 h-4" />
          Flagged/Suspended
        </button>
      </div>

      {activeTab === 'all' && renderUsersTable(users, total)}
      {activeTab === 'employers' && renderUsersTable(users.filter(u => u.account_type === 'employer'), total)}
      {activeTab === 'employees' && renderUsersTable(users.filter(u => u.account_type === 'employee'), total)}
      {activeTab === 'flagged' && renderFlaggedTable()}

      {/* User Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#1B5AFF]" />
              Full User Profile
            </DialogTitle>
          </DialogHeader>
          {profileLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 text-[#1B5AFF] animate-spin" />
            </div>
          ) : profileData ? (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <h3 className="text-[#5B6472] text-xs uppercase mb-2">Email</h3>
                  <p className="text-[#0A0F1E] font-medium">{profileData.user?.email}</p>
                </div>
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <h3 className="text-[#5B6472] text-xs uppercase mb-2">Account Type</h3>
                  <Badge className={profileData.user?.account_type === 'employer' ? 'bg-blue-500/20 text-blue-400 border-0' : 'bg-purple-500/20 text-purple-400 border-0'}>
                    {profileData.user?.account_type}
                  </Badge>
                </div>
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <h3 className="text-[#5B6472] text-xs uppercase mb-2">Joined</h3>
                  <p className="text-[#0A0F1E]">{profileData.user?.created_at ? new Date(profileData.user.created_at).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <h3 className="text-[#5B6472] text-xs uppercase mb-2">VETT ID</h3>
                  <p className="text-[#0A0F1E] font-mono">{profileData.user?.vett_id || 'N/A'}</p>
                </div>
              </div>

              {/* Profile Info */}
              {profileData.profile && (
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <h3 className="text-[#0A0F1E] font-semibold mb-3">Profile Details</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {profileData.user?.account_type === 'employer' ? (
                      <>
                        <div>
                          <span className="text-[#5B6472] text-xs">Company Name</span>
                          <p className="text-[#0A0F1E]">{profileData.profile.company_name || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-[#5B6472] text-xs">Subscription</span>
                          <p className="text-[#0A0F1E]">{profileData.profile.subscription_tier || 'free'}</p>
                        </div>
                        <div>
                          <span className="text-[#5B6472] text-xs">Industry</span>
                          <p className="text-[#0A0F1E]">{profileData.profile.industry || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-[#5B6472] text-xs">Location</span>
                          <p className="text-[#0A0F1E]">{profileData.profile.location || 'N/A'}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <span className="text-[#5B6472] text-xs">Name</span>
                          <p className="text-[#0A0F1E]">{profileData.profile.first_name} {profileData.profile.last_name}</p>
                        </div>
                        <div>
                          <span className="text-[#5B6472] text-xs">Job Title</span>
                          <p className="text-[#0A0F1E]">{profileData.profile.job_title || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-[#5B6472] text-xs">Industry</span>
                          <p className="text-[#0A0F1E]">{profileData.profile.industry || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-[#5B6472] text-xs">Pro Status</span>
                          <Badge className={profileData.profile.is_pro ? 'bg-green-500/20 text-green-400 border-0' : 'bg-gray-500/20 text-gray-400 border-0'}>
                            {profileData.profile.is_pro ? 'Pro' : 'Free'}
                          </Badge>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Records */}
              {profileData.records && profileData.records.length > 0 && (
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <h3 className="text-[#0A0F1E] font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-cyan-400" />
                    VETT Records ({profileData.records.length})
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {profileData.records.slice(0, 10).map((record, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded bg-[#F5F8FF]">
                        <div>
                          <p className="text-[#0A0F1E] text-sm">{record.job_title}</p>
                          <p className="text-[#5B6472] text-xs">{record.company_name}</p>
                        </div>
                        <Badge className={
                          record.status === 'published' ? 'bg-green-500/20 text-green-400 border-0' :
                          record.status === 'pending_acknowledgment' ? 'bg-amber-500/20 text-amber-400 border-0' :
                          'bg-gray-500/20 text-gray-400 border-0'
                        }>
                          {record.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Billing History (Employers) */}
              {profileData.billing_history && profileData.billing_history.length > 0 && (
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <h3 className="text-[#0A0F1E] font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-green-400" />
                    Billing History ({profileData.billing_history.length})
                  </h3>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {profileData.billing_history.map((tx, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded bg-[#F5F8FF]">
                        <div>
                          <p className="text-[#0A0F1E] text-sm">{tx.description || tx.plan_id}</p>
                          <p className="text-[#5B6472] text-xs">{new Date(tx.created_at).toLocaleDateString()}</p>
                        </div>
                        <span className="text-green-400 font-medium">€{(tx.amount / 100).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* VETT Score (Employees) */}
              {profileData.vett_score && (
                <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                  <h3 className="text-[#0A0F1E] font-semibold mb-3">VETT Score</h3>
                  <div className="flex items-center gap-4">
                    <div className="text-4xl font-bold text-[#1B5AFF]">{profileData.vett_score.total_score || 0}</div>
                    <div className="text-[#5B6472] text-sm">
                      Last updated: {profileData.vett_score.updated_at ? new Date(profileData.vett_score.updated_at).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                </div>
              )}

              {/* Cookie Consent */}
              <div className="p-4 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5]">
                <h3 className="text-[#0A0F1E] font-semibold mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-400" />
                  Cookie Consent
                </h3>
                {profileData.user?.cookie_consent ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${profileData.user.cookie_consent.essential ? 'bg-green-500' : 'bg-gray-500'}`} />
                        <span className="text-[#5B6472] text-sm">Essential</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${profileData.user.cookie_consent.analytics ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-[#5B6472] text-sm">Analytics</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${profileData.user.cookie_consent.marketing ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-[#5B6472] text-sm">Marketing</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${profileData.user.cookie_consent.preferences ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-[#5B6472] text-sm">Preferences</span>
                      </div>
                    </div>
                    <p className="text-[#5B6472] text-xs">
                      Consent given: {profileData.user.cookie_consent.timestamp ? new Date(profileData.user.cookie_consent.timestamp).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                ) : (
                  <p className="text-[#5B6472] text-sm">No cookie consent recorded</p>
                )}
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-[#E8EDF5]">
                <Button
                  onClick={() => {
                    setShowProfileModal(false);
                    setSelectedUser(profileData.user);
                    setShowMessageModal(true);
                  }}
                  className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
                <Button
                  onClick={() => handleTriggerPasswordReset(profileData.user?.id)}
                  className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Reset Password
                </Button>
                <Button
                  onClick={() => {
                    setShowProfileModal(false);
                    openSubscriptionModal(profileData.user);
                  }}
                  className="bg-purple-500/20 text-purple-400 hover:bg-purple-500/30"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Change Plan
                </Button>
                <Button
                  onClick={() => {
                    setSelectedUser(profileData.user);
                    setShowDeleteModal(true);
                  }}
                  className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                  disabled={profileData.user?.is_admin}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Account
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-[#5B6472] py-4">No profile data available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Subscription Modal */}
      <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
          <DialogHeader>
            <DialogTitle>Manage Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-[#5B6472]">User: {selectedUser?.email}</p>
            
            {selectedUser?.account_type === 'employer' ? (
              <>
                <Select value={subscriptionTier} onValueChange={setSubscriptionTier}>
                  <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E]">
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                    <SelectItem value="free" className="text-[#0A0F1E]">Free</SelectItem>
                    <SelectItem value="trial" className="text-[#0A0F1E]">Trial</SelectItem>
                    <SelectItem value="starter" className="text-[#0A0F1E]">Starter (€299/mo)</SelectItem>
                    <SelectItem value="growth" className="text-[#0A0F1E]">Growth (€799/mo)</SelectItem>
                  </SelectContent>
                </Select>
                
                {subscriptionTier === 'trial' && (
                  <div>
                    <label className="text-sm text-[#5B6472]">Trial Days</label>
                    <Input
                      type="number"
                      value={trialDays}
                      onChange={(e) => setTrialDays(parseInt(e.target.value))}
                      className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E]"
                      min={1}
                      max={90}
                    />
                  </div>
                )}
              </>
            ) : (
              <Select value={subscriptionTier} onValueChange={setSubscriptionTier}>
                <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E]">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                  <SelectItem value="free" className="text-[#0A0F1E]">Free</SelectItem>
                  <SelectItem value="pro" className="text-[#0A0F1E]">Pro (€9.90/mo)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowSubscriptionModal(false)}
              className="text-[#5B6472]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSubscription}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
            >
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Extend Trial Modal */}
      <Dialog open={showBulkTrialModal} onOpenChange={setShowBulkTrialModal}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Extend Trial
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-[#5B6472]">
              Extend trial for {selectedUsers.length} selected user(s)
            </p>
            <div>
              <label className="text-sm text-[#5B6472]">Days to add</label>
              <Input
                type="number"
                value={bulkTrialDays}
                onChange={(e) => setBulkTrialDays(parseInt(e.target.value) || 3)}
                className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowBulkTrialModal(false)} className="text-[#5B6472]">
              Cancel
            </Button>
            <Button onClick={handleBulkExtendTrial} className="bg-amber-500 hover:bg-amber-600 text-[#0A0F1E]">
              Extend Trials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              Send Message
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-[#5B6472]">To: {selectedUser?.email}</p>
            <div>
              <label className="text-sm text-[#5B6472]">Subject</label>
              <Input
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                placeholder="Message subject..."
                className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E]"
              />
            </div>
            <div>
              <label className="text-sm text-[#5B6472]">Message</label>
              <textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder="Write your message..."
                rows={5}
                className="mt-1 w-full px-3 py-2 rounded-lg bg-[#F5F8FF]/50 border border-[#E8EDF5] text-[#0A0F1E] resize-none focus:outline-none focus:border-[#1B5AFF]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowMessageModal(false)} className="text-[#5B6472]">
              Cancel
            </Button>
            <Button onClick={handleSendMessage} className="bg-blue-500 hover:bg-blue-600 text-[#0A0F1E]">
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={(open) => {
        setShowDeleteModal(open);
        if (!open) {
          setDeleteReason('');
          setDeleteConfirm('');
          setHardDelete(false);
        }
      }}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete User Account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm font-medium">Warning: This action cannot be undone!</p>
              <p className="text-[#5B6472] text-sm mt-1">User: {selectedUser?.email}</p>
            </div>
            
            <div>
              <label className="text-[#5B6472] text-sm">Reason for deletion</label>
              <Input
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Enter reason for audit log..."
                className="mt-1 bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <Checkbox
                id="hardDelete"
                checked={hardDelete}
                onCheckedChange={(checked) => setHardDelete(checked)}
                className="border-red-500"
              />
              <label htmlFor="hardDelete" className="text-sm">
                <span className="text-red-400 font-medium">Permanent deletion</span>
                <span className="text-[#5B6472] block text-xs">
                  Check to permanently remove all data. Uncheck to anonymize (GDPR compliant).
                </span>
              </label>
            </div>
            
            <div>
              <label className="text-[#5B6472] text-sm">Type DELETE to confirm</label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                className="mt-1 bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)} className="text-[#5B6472]">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteUser} 
              disabled={deleteConfirm !== 'DELETE' || deleting}
              className="bg-red-500 hover:bg-red-600 hover:bg-red-700 text-[#FFFFFF] disabled:opacity-50"
            >
              {deleting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              {hardDelete ? 'Permanently Delete' : 'Anonymize User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminUsers;
